import { useState, useCallback, useMemo, useEffect } from "react";
import type {
  EmailStatus,
  EmailListState,
  Attachment,
  DigiFiCustomer,
} from "src/types/conditionsInbox";
import { useApiClient, useSafeApiCall } from "src/hooks/api";
import { useAuth } from "src/context";
import type { AllEmailsResponse, EmailDetailsResponse } from "src/types/api";
import {
  convertApiEmailToEmailItem,
  convertApiAttachmentsToAttachments,
} from "src/utils/emails";
import { API_PREFIX_PATH } from "src/constants";

export function useConditionsInboxState() {
  const { fetchFromApi } = useApiClient();
  const safeApiCall = useSafeApiCall();
  const { user } = useAuth();

  const [state, setState] = useState<EmailListState>({
    emails: [],
    selectedEmailId: null,
    showCompleted: false,
    isLoading: false,
    error: null,
  });

  const [attachmentsCache, setAttachmentsCache] = useState<
    Record<string, Attachment[]>
  >({});

  const [renamedAttachments, setRenamedAttachments] = useState<
    Record<string, string>
  >({});

  const selectedEmail = useMemo(() => {
    if (!state.selectedEmailId) return null;
    return (
      state.emails.find((email) => email.id === state.selectedEmailId) || null
    );
  }, [state.selectedEmailId, state.emails]);

  const selectedEmailAttachments = useMemo(() => {
    if (!state.selectedEmailId) return [];
    return attachmentsCache[state.selectedEmailId] || [];
  }, [state.selectedEmailId, attachmentsCache]);

  const filteredEmails = useMemo(() => {
    if (state.showCompleted) {
      return state.emails.filter(
        (email) => email.status === "processed" || email.status === "junk",
      );
    } else {
      return state.emails.filter((email) => email.status === "not_processed");
    }
  }, [state.emails, state.showCompleted]);

  const fetchEmails = useCallback(() => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    safeApiCall(() => fetchFromApi(`${API_PREFIX_PATH}/conditions/emails`), {
      toastErrorMsg: "Failed to load emails",
      useServerErrorAsToast: true,
    })
      .then((response) => {
        const emailResponse = response as AllEmailsResponse;
        const allEmails = [
          ...emailResponse.not_processed.map(convertApiEmailToEmailItem),
          ...emailResponse.processed.map(convertApiEmailToEmailItem),
        ];

        setState((prev) => ({
          ...prev,
          emails: allEmails.map((newEmail) => {
            const existingEmail = prev.emails.find((e) => e.id === newEmail.id);
            console.log("Existing email found:", existingEmail);
            return existingEmail?.body
              ? { ...newEmail, body: existingEmail.body }
              : newEmail;
          }),
          isLoading: false,
        }));
      })
      .catch((err) => {
        setState((prev) => ({
          ...prev,
          emails: [],
          isLoading: false,
          error: err instanceof Error ? err.message : "Failed to load emails",
        }));
      });
  }, [user?.email]);

  const loadEmailDetails = useCallback(
    (emailId: string) => {
      if (attachmentsCache[emailId]) return;

      safeApiCall(
        () =>
          fetchFromApi(
            `${API_PREFIX_PATH}/conditions/emails/${parseInt(emailId)}`,
          ),
        { suppressErrorRedirect: true, suppressDuplicateToasts: true },
      )
        .then((response) => {
          const emailDetails = response as EmailDetailsResponse;

          if (emailDetails?.attachments?.length > 0) {
            const attachments = convertApiAttachmentsToAttachments(
              emailDetails.attachments,
            );
            setAttachmentsCache((prev) => ({
              ...prev,
              [emailId]: attachments,
            }));
          }

          setState((prev) => ({
            ...prev,
            emails: prev.emails.map((email) =>
              email.id === emailId
                ? { ...email, body: emailDetails.email_body }
                : email,
            ),
          }));
        })
        .catch((err) => {
          console.warn("Failed to load email details:", err);
          setAttachmentsCache((prev) => ({ ...prev, [emailId]: [] }));
        });
    },
    [user?.email],
  );

  const markEmailAsJunk = useCallback(
    (emailId: string) => {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      safeApiCall(
        () =>
          fetchFromApi(
            `${API_PREFIX_PATH}/conditions/emails/${parseInt(emailId)}/junk`,
            {
              method: "PUT",
              body: { processed_by: user?.email || "" },
            },
          ),
        {
          toastSuccessMsg: "Email marked as junk",
          toastErrorMsg: "Failed to mark email as junk",
          suppressErrorRedirect: true,
        },
      )
        .then(() => {
          setState((prev) => ({
            ...prev,
            emails: prev.emails.map((email) =>
              email.id === emailId
                ? { ...email, status: "junk" as EmailStatus }
                : email,
            ),
            isLoading: false,
          }));
        })
        .catch((err) => {
          setState((prev) => ({
            ...prev,
            isLoading: false,
            error:
              err instanceof Error
                ? err.message
                : "Failed to mark email as junk",
          }));
        });
    },
    [user?.email],
  );

  const sendFiles = useCallback(
    (emailId: string, attachmentsId: Set<string>, customer: DigiFiCustomer) => {
      if (!emailId || !customer)
        return Promise.reject(new Error("Missing required parameters"));

      const emailAttachments = attachmentsCache[emailId] || [];
      const sentAttachments = emailAttachments
        .filter((attachment) => attachmentsId.has(attachment.id))
        .map((attachment) => ({
          attachment_id: parseInt(attachment.id),
          name: renamedAttachments[attachment.id] || attachment.name,
        }));

      return safeApiCall(
        () =>
          fetchFromApi(
            `${API_PREFIX_PATH}/conditions/emails/${parseInt(emailId)}/send`,
            {
              method: "POST",
              body: {
                processed_by: user?.email || "",
                los_id: customer.id,
                sent_attachments: sentAttachments,
              },
            },
          ),
        {
          toastSuccessMsg: `Files sent successfully to ${customer.name}`,
          toastErrorMsg: "Failed to send files",
          suppressErrorRedirect: true,
        },
      ).then(() => {
        setState((prev) => ({
          ...prev,
          emails: prev.emails.map((email) =>
            email.id === emailId
              ? { ...email, status: "processed" as EmailStatus }
              : email,
          ),
        }));
      });
    },
    [user?.email, attachmentsCache, renamedAttachments],
  );

  const updateAttachmentName = useCallback(
    (attachmentId: string, newName: string) => {
      setRenamedAttachments((prev) => ({ ...prev, [attachmentId]: newName }));
    },
    [],
  );

  const fetchAttachmentSignedUrl = useCallback(
    (attachmentId: string): Promise<string> => {
      return safeApiCall(
        () =>
          fetchFromApi(
            `${API_PREFIX_PATH}/conditions/attachments/${parseInt(attachmentId)}`,
          ),
        { suppressErrorRedirect: true, suppressDuplicateToasts: true },
      ).then((response: any) => {
        return response.object_store_url;
      });
    },
    [],
  );

  useEffect(() => {
    fetchEmails();
  }, [user?.email]);

  return {
    ...state,
    emails: filteredEmails,
    selectedEmail,
    selectedEmailAttachments,
    markEmailAsJunk,
    sendFiles,
    fetchEmails,
    loadEmailDetails,
    updateAttachmentName,
    fetchAttachmentSignedUrl,
    renamedAttachments,
    setState,
  };
}
